import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  ElementRef,
  input,
  output,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { UiTooltip } from '../tooltip/tooltip.directive';

const DAYS_ES = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MONTHS_ES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

export interface DatePickerButtonBarContext {
  clearCallback: (event: MouseEvent) => void;
}

@Component({
  selector: 'app-datepicker',
  imports: [UiTooltip],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiDatepicker),
      multi: true,
    },
  ],
  template: `
    <div class="relative w-full" [class]="styleClass()">
      <input
        type="text"
        [id]="inputId()"
        [placeholder]="placeholder()"
        [readonly]="true"
        [disabled]="disabled()"
        [class]="inputClasses()"
        [value]="displayValue()"
        (click)="toggle()"
        [attr.aria-label]="ariaLabel() || placeholder()"
        [attr.aria-expanded]="isOpen()"
        [attr.aria-invalid]="invalid()"
      />

      @if (isOpen()) {
        <div class="fixed inset-0 z-9998" (click)="close()" aria-hidden="true"></div>
        <div
          class="fixed z-9999 bg-white border border-slate-200 rounded-xl shadow-lg p-3 w-72 animate-fade-in max-h-[80vh] overflow-y-auto"
          [style.top.px]="dropdownTop()"
          [style.left.px]="dropdownLeft()"
          role="dialog"
          aria-label="Selector de fecha"
        >
          <!-- Header -->
          <div class="flex items-center justify-between mb-2">
            <button
              type="button"
              class="p-1.5 rounded-md hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
              (click)="prevMonth()"
              aria-label="Mes anterior"
            >
              <i class="bi bi-chevron-left" aria-hidden="true"></i>
            </button>
            <span class="text-sm font-semibold text-slate-800 capitalize">
              {{ months[currentMonth()] }} {{ currentYear() }}
            </span>
            <button
              type="button"
              class="p-1.5 rounded-md hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
              (click)="nextMonth()"
              aria-label="Mes siguiente"
            >
              <i class="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
          </div>

          <!-- Day names -->
          <div class="grid grid-cols-7 gap-0.5 mb-1">
            @for (day of days; track $index) {
              <span class="text-xs text-center text-gray-500 font-medium py-1">{{ day }}</span>
            }
          </div>

          <!-- Calendar grid -->
          <div class="grid grid-cols-7 gap-0.5">
            @for (cell of calendarCells(); track $index) {
              @if (cell.date) {
                <button
                  type="button"
                  [class]="dayClasses(cell.date)"
                  [disabled]="isDisabledDate(cell.date)"
                  (click)="selectDate(cell.date)"
                  [attr.aria-label]="formatAriaDate(cell.date)"
                  [attr.aria-selected]="isSelected(cell.date)"
                >
                  {{ cell.date.getDate() }}
                </button>
              } @else {
                <span class="p-1.5"></span>
              }
            }
          </div>

          <!-- Button bar -->
          @if (showButtonBar()) {
            <div
              class="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between gap-2"
            >
              <div class="flex items-center gap-1.5">
                <button
                  type="button"
                  class="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-accent-50 hover:border-accent-200 hover:text-accent-600 transition-colors"
                  (click)="setShortcut('today')"
                  appTooltip="Hoy"
                  tooltipPosition="top"
                >
                  <i class="bi bi-calendar-check text-sm"></i>
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-accent-50 hover:border-accent-200 hover:text-accent-600 transition-colors"
                  (click)="setShortcut('week')"
                  appTooltip="Últimos 7 días"
                  tooltipPosition="top"
                >
                  <i class="bi bi-calendar-week text-sm"></i>
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-accent-50 hover:border-accent-200 hover:text-accent-600 transition-colors"
                  (click)="setShortcut('month')"
                  appTooltip="Este mes"
                  tooltipPosition="top"
                >
                  <i class="bi bi-calendar-month text-sm"></i>
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-accent-50 hover:border-accent-200 hover:text-accent-600 transition-colors"
                  (click)="setShortcut('lastMonth')"
                  appTooltip="Mes pasado"
                  tooltipPosition="top"
                >
                  <i class="bi bi-arrow-return-left text-sm"></i>
                </button>
              </div>
              <button
                type="button"
                class="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                (click)="clear($event)"
              >
                <i class="bi bi-x-circle mr-1"></i>Limpiar
              </button>
            </div>
          }
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UiDatepicker {
  readonly placeholder = input<string>('Seleccione fecha');
  readonly dateFormat = input<string>('dd/mm/yy');
  readonly selectionMode = input<'single' | 'range'>('single');
  readonly minDate = input<Date | null>(null);
  readonly maxDate = input<Date | null>(null);
  readonly showButtonBar = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly inputId = input<string>('datepicker-' + Math.random().toString(36).substring(2, 9));
  readonly ariaLabel = input<string>('');
  readonly styleClass = input<string>('');
  readonly borderless = input<boolean>(false);

  readonly onSelect = output<Date | Date[] | null>();

  protected readonly isOpen = signal(false);
  protected readonly dropdownTop = signal(0);
  protected readonly dropdownLeft = signal(0);
  protected readonly value = signal<Date | Date[] | null>(null);
  protected readonly currentMonth = signal(new Date().getMonth());
  protected readonly currentYear = signal(new Date().getFullYear());

  protected readonly days = DAYS_ES;
  protected readonly months = MONTHS_ES;

  private elementRef = inject(ElementRef);

  onTouched: () => void = () => {};
  private onChange: (value: Date | Date[] | null) => void = () => {};

  constructor() {
    // Initialize calendar view to the selected date
    effect(() => {
      const v = this.value();
      if (v instanceof Date) {
        this.currentMonth.set(v.getMonth());
        this.currentYear.set(v.getFullYear());
      } else if (Array.isArray(v) && v[0] instanceof Date) {
        this.currentMonth.set(v[0].getMonth());
        this.currentYear.set(v[0].getFullYear());
      }
    });
  }

  protected readonly displayValue = computed(() => {
    const v = this.value();
    if (!v) return '';
    if (Array.isArray(v)) {
      if (!v[0]) return '';
      const start = this.formatDate(v[0]);
      const end = v[1] ? this.formatDate(v[1]) : '';
      return end ? `${start} - ${end}` : start;
    }
    return this.formatDate(v);
  });

  protected readonly calendarCells = computed(() => {
    const year = this.currentYear();
    const month = this.currentMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Monday-first offset (getDay: 0=Sunday)
    let offset = firstDay.getDay() - 1;
    if (offset < 0) offset = 6;

    const cells: { date: Date | null }[] = [];
    for (let i = 0; i < offset; i++) {
      cells.push({ date: null });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(year, month, d) });
    }
    return cells;
  });

  protected inputClasses(): string {
    if (this.borderless()) {
      return 'w-full px-3 py-2 text-sm border-0 rounded-none bg-transparent focus:outline-none focus:ring-0 cursor-pointer text-slate-700 placeholder:text-slate-400';
    }
    const base =
      'w-full px-3 py-2 text-sm border rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500 cursor-pointer';
    const normal = 'border-slate-200 hover:border-slate-300 bg-white';
    const invalidCls = 'border-red-400 focus:ring-red-500/20 focus:border-red-500 bg-white';
    const disabled = this.disabled() ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : '';
    return `${base} ${this.invalid() ? invalidCls : normal} ${disabled}`;
  }

  protected dayClasses(date: Date): string {
    const base =
      'p-1.5 text-sm rounded-md text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500';
    if (this.isDisabledDate(date)) {
      return `${base} text-slate-300 cursor-not-allowed`;
    }
    if (this.isSelected(date)) {
      return `${base} bg-accent-600 text-white font-medium`;
    }
    if (this.isInRange(date)) {
      return `${base} bg-accent-100 text-accent-800`;
    }
    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
    return `${base} hover:bg-slate-100 text-slate-700 ${isToday ? 'ring-1 ring-accent-400' : ''}`;
  }

  protected isSelected(date: Date): boolean {
    const v = this.value();
    if (!v) return false;
    if (Array.isArray(v)) {
      return (
        (v[0] instanceof Date && this.sameDay(date, v[0])) ||
        (v[1] instanceof Date && this.sameDay(date, v[1]))
      );
    }
    return this.sameDay(date, v);
  }

  protected isInRange(date: Date): boolean {
    const v = this.value();
    if (!Array.isArray(v) || !(v[0] instanceof Date) || !(v[1] instanceof Date)) return false;
    return date > v[0] && date < v[1];
  }

  protected isDisabledDate(date: Date): boolean {
    const min = this.minDate();
    const max = this.maxDate();
    if (min && this.stripTime(date) < this.stripTime(min)) return true;
    if (max && this.stripTime(date) > this.stripTime(max)) return true;
    return false;
  }

  protected selectDate(date: Date): void {
    if (this.isDisabledDate(date)) return;

    if (this.selectionMode() === 'range') {
      const v = this.value();
      if (!Array.isArray(v) || (v[0] && v[1])) {
        this.setValue([date, null as any]);
      } else if (v[0]) {
        if (date < v[0]) {
          this.setValue([date, v[0]]);
        } else {
          this.setValue([v[0], date]);
        }
        this.onSelect.emit(this.value());
        this.close();
        return;
      }
      this.onSelect.emit(this.value());
    } else {
      this.setValue(date);
      this.onSelect.emit(date);
      this.close();
    }
  }

  protected prevMonth(): void {
    if (this.currentMonth() === 0) {
      this.currentMonth.set(11);
      this.currentYear.update((y) => y - 1);
    } else {
      this.currentMonth.update((m) => m - 1);
    }
  }

  protected nextMonth(): void {
    if (this.currentMonth() === 11) {
      this.currentMonth.set(0);
      this.currentYear.update((y) => y + 1);
    } else {
      this.currentMonth.update((m) => m + 1);
    }
  }

  toggle(): void {
    if (this.disabled()) return;
    const opening = !this.isOpen();
    if (opening) {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      const calendarHeight = 380;
      const calendarWidth = 288;
      const spaceBelow = window.innerHeight - rect.bottom;

      if (spaceBelow < calendarHeight) {
        this.dropdownTop.set(Math.max(4, rect.top - calendarHeight - 4));
      } else {
        this.dropdownTop.set(rect.bottom + 4);
      }

      let left = rect.left;
      if (left + calendarWidth > window.innerWidth) {
        left = Math.max(4, window.innerWidth - calendarWidth - 4);
      }
      this.dropdownLeft.set(Math.max(4, left));
    }
    this.isOpen.update((v) => !v);
  }

  close(): void {
    this.isOpen.set(false);
    this.onTouched();
  }

  clear(event: MouseEvent): void {
    event.stopPropagation();
    this.setValue(null);
    this.onSelect.emit(null);
  }

  protected setShortcut(shortcut: 'today' | 'week' | 'month' | 'lastMonth'): void {
    const today = this.stripTime(new Date());
    let start: Date;
    let end: Date;

    switch (shortcut) {
      case 'today':
        start = today;
        end = today;
        break;
      case 'week':
        start = new Date(today);
        start.setDate(start.getDate() - 6);
        end = today;
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
    }

    if (this.selectionMode() === 'range') {
      this.setValue([start, end]);
      this.onSelect.emit([start, end]);
    } else {
      this.setValue(end);
      this.onSelect.emit(end);
    }

    this.currentMonth.set(start.getMonth());
    this.currentYear.set(start.getFullYear());
    this.close();
  }

  private setValue(value: Date | string | (Date | string)[] | null): void {
    const normalized = this.normalizeDateValue(value);
    this.value.set(normalized);
    this.onChange(normalized);
  }

  private normalizeDateValue(value: Date | string | (Date | string)[] | null): Date | Date[] | null {
    if (!value) return null;
    if (Array.isArray(value)) {
      return value.map((v) => this.toDate(v)).filter((v): v is Date => v !== null);
    }
    return this.toDate(value);
  }

  private toDate(v: Date | string | null): Date | null {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  private sameDay(a: Date, b: Date): boolean {
    return (
      a.getDate() === b.getDate() &&
      a.getMonth() === b.getMonth() &&
      a.getFullYear() === b.getFullYear()
    );
  }

  private stripTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  protected formatAriaDate(date: Date): string {
    return `${date.getDate()} de ${MONTHS_ES[date.getMonth()]} de ${date.getFullYear()}`;
  }

  private formatDate(date: Date | string): string {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    const fmt = this.dateFormat();
    return fmt.replace('dd', dd).replace('mm', mm).replace('yy', String(yy));
  }

  // ControlValueAccessor
  writeValue(value: Date | string | (Date | string)[] | null): void {
    this.value.set(this.normalizeDateValue(value));
  }

  registerOnChange(fn: (value: Date | Date[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(): void {
    // handled via input
  }
}
